/** The type of errors that can be encountered while decoding. */
export type DecodeError = ErrorMessage | TypeMismatch | ErrorAtIndex | ErrorAtProperty;

/** A basic error message. */
type ErrorMessage = ['ErrorMessage', string];
export const ErrorMessage = (message: string): DecodeError => ['ErrorMessage', message];

/** A type mismatch error. */
type TypeMismatch = ['TypeMismatch', string, string];
export const TypeMismatch = (expected: string, found: string): DecodeError => ['TypeMismatch', expected, found];

/** An error at a specific index in an array. */
type ErrorAtIndex = ['ErrorAtIndex', number, DecodeError];
export const ErrorAtIndex = (index: number, error: DecodeError): DecodeError => ['ErrorAtIndex', index, error];

/** An error at a specific property in an object. */
type ErrorAtProperty = ['ErrorAtProperty', string, DecodeError];
export const ErrorAtProperty = (property: string, error: DecodeError): DecodeError => [
    'ErrorAtProperty',
    property,
    error,
];

/** Formats a decoding error into a human-readable string. */
export const formatError = (error: DecodeError): string => {
    switch (error[0]) {
        case 'ErrorMessage':
            return error[1];
        case 'ErrorAtIndex':
            return 'Error at array index ' + error[1] + ': ' + formatError(error[2]);
        case 'ErrorAtProperty':
            return 'Error at property ' + error[1] + ': ' + formatError(error[2]);
        case 'TypeMismatch':
            return 'Type mismatch: expected ' + error[1] + ', found ' + error[2];
    }
};

/** A non-empty list of errors. */
export interface MultipleErrors extends ReadonlyArray<DecodeError> {
    readonly 0: DecodeError;
}

/** A decoding error class. Contains a list of 1+ decoding errors. */
export class DecodingError extends Error {
    constructor(public readonly errors: MultipleErrors) {
        super();
        if (this.errors.length === 0) {
            throw new Error('DecodingError must have at least one error');
        }
    }

    // eslint-disable-next-line @typescript-eslint/class-literal-property-style
    override get name() {
        return 'DecodingError';
    }

    override get message() {
        return `The following errors were encountered:\n${this.errors
            .map((error) => `  - ${formatError(error)}`)
            .join('\n')}`;
    }

    /** Creates a decoding error from a single error message. */
    static readonly errorMessage = (message: string) => new DecodingError([ErrorMessage(message)]);

    /** Creates a decoding error from a type mismatch error. */
    static readonly typeMismatch = (expected: string, found: string) =>
        new DecodingError([TypeMismatch(expected, found)]);

    /** Transforms all contained errors into errors at a specific index in an array. */
    static readonly errorAtIndex = (index: number, error: DecodingError) =>
        new DecodingError(error.errors.map((error) => ErrorAtIndex(index, error)) as unknown as MultipleErrors);

    /** Transforms all contained errors into errors at a specific property in an object. */
    static readonly errorAtProperty = (property: string, error: DecodingError) =>
        new DecodingError(error.errors.map((error) => ErrorAtProperty(property, error)) as unknown as MultipleErrors);

    /** Folds a list of decoding errors into a single decoding error. */
    static readonly fold = (errors: DecodingError[]): DecodingError =>
        new DecodingError(errors.flatMap((error) => error.errors) as unknown as MultipleErrors);

    /** Maps all contained errors using the provided function. */
    map(f: (error: DecodeError) => DecodeError): DecodingError {
        return new DecodingError(this.errors.map(f) as unknown as MultipleErrors);
    }

    /** Creates a new decoding error using the combined errors of the current and provided errors. */
    append(other: DecodingError): DecodingError {
        return new DecodingError([...this.errors, ...other.errors] as unknown as MultipleErrors);
    }
}
